/**
 * STT 테스트용 유틸리티: 로컬 오디오 파일을 마이크 스트림에 믹싱하여 Jitsi/Jigasi로 송출.
 * 원본: wehago-conference/src/init.js (window.simulateTTS)
 *
 * 사용법: 브라우저 콘솔에서 simulateTTS('ko' | 'ja' | 'en') 실행
 */
import { getConferenceManager } from '@/conference/conferenceManagerRef';
import testAudioEnFile from '@/media/openAI-en.mp3';
import testAudioJaFile from '@/media/openAI-ja.mp3';
import testAudioKoFile from '@/media/openAI-ko.mp3';

type TTSLanguage = 'ko' | 'ja' | 'en';

const TTS_TEST_AUDIO_FILES: Record<TTSLanguage, string> = {
  en: testAudioEnFile,
  ja: testAudioJaFile,
  ko: testAudioKoFile
};

function normalizeTTSLanguage(language?: string): TTSLanguage {
  const normalized = language?.trim().toLowerCase();

  if (normalized === 'en' || normalized === 'english') {
    return 'en';
  }

  if (normalized === 'ja' || normalized === 'jp' || normalized === 'japanese') {
    return 'ja';
  }

  return 'ko';
}

class TTSEffect {
  private audioContext: AudioContext | null = null;
  private destination: MediaStreamAudioDestinationNode | null = null;
  private micSource: MediaStreamAudioSourceNode | null = null;
  private ttsSource: MediaElementAudioSourceNode | null = null;
  private readonly audioFile: string;
  audioElement: HTMLAudioElement | null = null;

  constructor(audioFile: string) {
    this.audioFile = audioFile;
  }

  isEnabled() {
    return true;
  }

  startEffect(originalStream: MediaStream): MediaStream {
    this.audioContext = new AudioContext();
    this.destination = this.audioContext.createMediaStreamDestination();

    // 마이크 소스 연결
    this.micSource = this.audioContext.createMediaStreamSource(originalStream);
    this.micSource.connect(this.destination);

    // 로컬 오디오 파일 소스 연결
    this.audioElement = new Audio();
    this.audioElement.src = this.audioFile;

    this.ttsSource = this.audioContext.createMediaElementSource(this.audioElement);
    this.ttsSource.connect(this.destination);

    this.audioElement.play().catch((e) => console.error('[TTS Simulator] Play error:', e));
    return this.destination.stream;
  }

  stopEffect() {
    try {
      if (this.audioElement) this.audioElement.pause();
      if (this.micSource) this.micSource.disconnect();
      if (this.ttsSource) this.ttsSource.disconnect();
      if (this.audioContext && this.audioContext.state !== 'closed') this.audioContext.close();
    } catch { /* ignore */ }
  }
}

async function simulateTTS(language: TTSLanguage | string = 'ko'): Promise<void> {
  try {
    const selectedLanguage = normalizeTTSLanguage(language);
    const selectedAudioFile = TTS_TEST_AUDIO_FILES[selectedLanguage];

    const mgr = getConferenceManager();
    const connector = mgr?.getConnector();
    if (!connector) {
      console.error('[TTS Simulator] Room not found.');
      return;
    }

    const localAudioTrack = connector.getLocalAudioTrack();
    if (!localAudioTrack) {
      console.error('[TTS Simulator] Local audio track not found. Please turn on your microphone.');
      return;
    }

    console.log(`[TTS Simulator] Starting local ${selectedLanguage} test audio file playback...`);

    const effect = new TTSEffect(selectedAudioFile);
    await localAudioTrack.setEffect(effect);
    console.log(`[TTS Simulator] TTS effect applied and speaking (${selectedLanguage}).`);

    if (effect.audioElement) {
      effect.audioElement.onended = async () => {
        console.log('[TTS Simulator] TTS playback finished. Restoring original microphone stream.');
        await localAudioTrack.setEffect(undefined);
      };

      effect.audioElement.onerror = async (e) => {
        console.error('[TTS Simulator] Audio element error:', e);
        await localAudioTrack.setEffect(undefined);
      };
    }
  } catch (error) {
    console.error('[TTS Simulator] Failed to simulate TTS:', error);
  }
}

// window에 등록하여 콘솔에서 직접 호출 가능
(window as unknown as Record<string, unknown>).simulateTTS = simulateTTS;

export default simulateTTS;
